import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/AddOutlined';
import KeyIcon from '@mui/icons-material/KeyOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { PERMISSIONS } from '@/types/auth';
import type { HardwareStationDto, SaveHardwareStationRequest } from '@/types/weightProduction';

// Everything hardware-related defaults to null on purpose: null means "decide locally", which is
// what makes one identical config file work on every station PC.
const STATION_DEFAULTS: SaveHardwareStationRequest = {
  name: '', code: '', plantArea: null, status: 'Active',
  serialPortName: null, baudRate: 9600, dataBits: 8, parity: 'None', stopBits: 1,
  softStableReadings: 3, parseRegex: null, printerName: null, heartbeatSeconds: 20,
};

/**
 * Weighing stations — the named points a capture is attributed to.
 *
 * This screen used to also hold Weighing Machine and Printer profiles (COM ports, baud rates,
 * printer names). They were removed because nothing read them: the Hardware Bridge agent on each
 * station's PC owns those settings, since they describe that machine rather than company data, and a
 * second unread copy here only invited the two to disagree.
 */
export default function HardwareConfigurationPage() {
  const { t } = useTranslation('weightProduction');
  const queryClient = useQueryClient();

  const { data: stations = [], isLoading } = useQuery({
    queryKey: ['hardware-stations'],
    queryFn: async () => (await apiClient.get<HardwareStationDto[]>('/hardware-stations')).data,
  });

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<SaveHardwareStationRequest>(STATION_DEFAULTS);

  // Whether a package has actually been staged on this server — the button explains itself when
  // there's nothing to hand out, instead of offering a download that fails.
  const { data: agentPackage } = useQuery({
    queryKey: ['hardware-agent-availability'],
    queryFn: async () =>
      (await apiClient.get<{ available: boolean; sizeBytes: number; lastUpdated: string | null }>('/hardware-agent/availability')).data,
  });

  // Fetched through apiClient (not a plain <a href>) so the bearer token goes with it — the
  // endpoint is permission-gated because this build carries the plant's enrollment key. Same
  // blob-then-click approach the invoice PDF download already uses.
  const downloadAgentMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.get('/hardware-agent/download', { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'hardware-agent.zip';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['hardware-stations'] });
  const saveMutation = useMutation({
    mutationFn: () => (editingId ? apiClient.put(`/hardware-stations/${editingId}`, form) : apiClient.post('/hardware-stations', form)),
    onSuccess: () => { invalidate(); setOpen(false); },
  });
  const deleteMutation = useMutation({ mutationFn: (id: string) => apiClient.delete(`/hardware-stations/${id}`), onSuccess: invalidate });

  // The key is returned in clear exactly once — the server stores it but the UI never fetches it
  // back, so it's held here only until the admin closes the dialog.
  const { data: enrollmentKey } = useQuery({
    queryKey: ['agent-enrollment-key'],
    // BaseApiController.FromResult<T> returns Ok(result.Data) — the bare payload, never a
    // {data,succeeded} envelope. Reading .data.data yielded undefined, which the field rendered as
    // "No key generated yet" even when a key existed.
    queryFn: async () => (await apiClient.get<string | null>('/hardware-monitor/enrollment-key')).data,
  });
  const generateEnrollmentKey = useMutation({
    mutationFn: () => apiClient.post('/hardware-monitor/enrollment-key'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['agent-enrollment-key'] }),
  });

  const [issuedKey, setIssuedKey] = useState<{ station: string; key: string } | null>(null);
  const generateKeyMutation = useMutation({
    mutationFn: async (station: HardwareStationDto) => {
      // Same bare-payload convention as the enrollment key above — the dialog was showing an empty
      // box for a key that had in fact been issued.
      const response = await apiClient.post<string>(`/hardware-stations/${station.id}/agent-key`);
      return { station: station.code, key: response.data };
    },
    onSuccess: (result) => { setIssuedKey(result); invalidate(); },
  });

  const columns: DataTableColumn<HardwareStationDto>[] = [
    { field: 'name', headerName: t('hardware.station.name') },
    { field: 'code', headerName: t('hardware.station.code') },
    { field: 'plantArea', headerName: t('hardware.station.plantArea'), render: (r) => r.plantArea ?? '—' },
    { field: 'machineName', headerName: t('hardware.station.machine'), render: (r) => r.machineName ?? '—' },
    {
      field: 'hardware',
      headerName: t('hardware.station.hardwareSection'),
      render: (r) => `${r.serialPortName ?? t('hardware.station.autoDetect')} · ${r.printerName ?? t('hardware.station.windowsDefault')}`,
    },
    { field: 'status', headerName: t('hardware.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'actions', headerName: '',
      render: (r) => (
        <IfPermitted permission={PERMISSIONS.WEIGHT_PRODUCTION_MANAGE}>
          <Stack direction="row" spacing={0.5}>
            <Button size="small" onClick={() => { setEditingId(r.id); setForm(r); setOpen(true); }}>{t('hardware.edit')}</Button>
            <Button size="small" startIcon={<KeyIcon fontSize="small" />} disabled={generateKeyMutation.isPending} onClick={() => generateKeyMutation.mutate(r)}>
              {t('hardware.generateKey')}
            </Button>
            <Button size="small" color="error" onClick={() => deleteMutation.mutate(r.id)}>{t('hardware.delete')}</Button>
          </Stack>
        </IfPermitted>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('hardware.title')}
        subtitle={t('hardware.subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.WEIGHT_PRODUCTION_MANAGE}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              disabled={!agentPackage?.available || downloadAgentMutation.isPending}
              onClick={() => downloadAgentMutation.mutate()}
              title={agentPackage?.available ? t('hardware.downloadAgentHint') : t('hardware.downloadAgentUnavailable')}
            >
              {downloadAgentMutation.isPending ? t('hardware.downloadingAgent') : t('hardware.downloadAgent')}
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingId(null); setForm(STATION_DEFAULTS); setOpen(true); }}>
              {t('hardware.add')}
            </Button>
          </IfPermitted>
        }
      />

      {/* The one credential every station PC shares. Deliberately re-readable, unlike a per-station
          key: it has to be copied onto every new machine, and hiding it would mean rotating it just
          to add a PC. */}
      <Card variant="outlined" sx={{ mb: 2, borderRadius: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <KeyIcon fontSize="small" color="primary" />
            <Typography sx={{ fontWeight: 800 }}>{t('hardware.enrollment.title')}</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('hardware.enrollment.explain')}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              fullWidth size="small" value={enrollmentKey ?? ''}
              placeholder={t('hardware.enrollment.notGenerated')}
              slotProps={{ input: { readOnly: true, sx: { fontFamily: 'monospace' } } }}
              onFocus={(e) => e.target.select()}
            />
            <Button size="small" disabled={!enrollmentKey} onClick={() => { void navigator.clipboard?.writeText(enrollmentKey ?? ''); }}>
              {t('hardware.copyKey')}
            </Button>
            <IfPermitted permission={PERMISSIONS.WEIGHT_PRODUCTION_MANAGE}>
              <Button size="small" variant="outlined" color="warning" disabled={generateEnrollmentKey.isPending} onClick={() => generateEnrollmentKey.mutate()}>
                {enrollmentKey ? t('hardware.enrollment.rotate') : t('hardware.enrollment.generate')}
              </Button>
            </IfPermitted>
          </Stack>
          {enrollmentKey && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
              {t('hardware.enrollment.rotateWarning')}
            </Typography>
          )}
          {/* Stated here because the per-row button otherwise reads as a required step for every PC,
              which is the opposite of the point. */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {t('hardware.enrollment.perStationNote')}
          </Typography>
        </CardContent>
      </Card>

      <Alert severity="info" sx={{ mb: 2 }}>{t('hardware.codeMatchNotice')}</Alert>

      <DataTable columns={columns} rows={stations} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="weighing-stations" />

      {/* Shown once. There is deliberately no way to read a key back later — if it's lost, generate
          a new one, which also revokes the old. */}
      <Dialog open={Boolean(issuedKey)} onClose={() => setIssuedKey(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('hardware.keyIssuedTitle', { code: issuedKey?.station ?? '' })}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>{t('hardware.keyIssuedWarning')}</Alert>
          <TextField
            fullWidth multiline value={issuedKey?.key ?? ''}
            slotProps={{ input: { readOnly: true, sx: { fontFamily: 'monospace', fontSize: 14 } } }}
            onFocus={(e) => e.target.select()}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            {t('hardware.keyIssuedWhere')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { void navigator.clipboard?.writeText(issuedKey?.key ?? ''); }}>{t('hardware.copyKey')}</Button>
          <Button variant="contained" onClick={() => setIssuedKey(null)}>{t('hardware.done')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? t('hardware.edit') : t('hardware.add')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label={t('hardware.station.name')} fullWidth value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <TextField
              label={t('hardware.station.code')} fullWidth value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              disabled={Boolean(editingId)}
              helperText={t('hardware.station.codeHelper')}
            />
            <TextField label={t('hardware.station.plantArea')} fullWidth value={form.plantArea ?? ''} onChange={(e) => setForm({ ...form, plantArea: e.target.value || null })} />
            <TextField select label={t('hardware.status')} fullWidth value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </TextField>

            {/* Read by that station's agent on every heartbeat — change a value here and the agent
                applies it within seconds, without anyone visiting the machine. */}
            <Typography variant="subtitle2" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6, pt: 1 }}>
              {t('hardware.station.hardwareSection')}
            </Typography>
            <Alert severity="info" sx={{ py: 0.5 }}>{t('hardware.station.blankMeansLocal')}</Alert>

            <Stack direction="row" spacing={1}>
              <TextField
                label={t('hardware.station.serialPort')} fullWidth value={form.serialPortName ?? ''}
                placeholder={t('hardware.station.autoDetect')}
                onChange={(e) => setForm({ ...form, serialPortName: e.target.value || null })}
              />
              <TextField
                type="number" label={t('hardware.station.baudRate')} sx={{ minWidth: 130 }} value={form.baudRate ?? ''}
                onChange={(e) => setForm({ ...form, baudRate: Number(e.target.value) || null })}
              />
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField
                type="number" label={t('hardware.station.dataBits')} fullWidth value={form.dataBits ?? ''}
                onChange={(e) => setForm({ ...form, dataBits: Number(e.target.value) || null })}
              />
              <TextField
                select label={t('hardware.station.parity')} fullWidth value={form.parity ?? 'None'}
                onChange={(e) => setForm({ ...form, parity: e.target.value })}
              >
                {['None', 'Odd', 'Even', 'Mark', 'Space'].map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
              <TextField
                type="number" label={t('hardware.station.stopBits')} fullWidth value={form.stopBits ?? ''}
                onChange={(e) => setForm({ ...form, stopBits: Number(e.target.value) || null })}
              />
            </Stack>
            <TextField
              label={t('hardware.station.printerName')} fullWidth value={form.printerName ?? ''}
              placeholder={t('hardware.station.windowsDefault')}
              onChange={(e) => setForm({ ...form, printerName: e.target.value || null })}
            />
            <Stack direction="row" spacing={1}>
              <TextField
                type="number" label={t('hardware.station.stableReadings')} fullWidth value={form.softStableReadings ?? ''}
                helperText={t('hardware.station.stableReadingsHint')}
                onChange={(e) => setForm({ ...form, softStableReadings: Number(e.target.value) || null })}
              />
              <TextField
                type="number" label={t('hardware.station.heartbeatSeconds')} fullWidth value={form.heartbeatSeconds ?? ''}
                onChange={(e) => setForm({ ...form, heartbeatSeconds: Number(e.target.value) || null })}
              />
            </Stack>
            <TextField
              label={t('hardware.station.parseRegex')} fullWidth value={form.parseRegex ?? ''}
              helperText={t('hardware.station.parseRegexHint')}
              onChange={(e) => setForm({ ...form, parseRegex: e.target.value || null })}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{t('hardware.cancel')}</Button>
          <Button variant="contained" disabled={!form.name || !form.code || saveMutation.isPending} onClick={() => saveMutation.mutate()}>{t('hardware.save')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
